import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ProtectedRoute, AdminRoute } from './components/RouteGuards';

const OtpRegistrationPage        = lazy(() => import('./features/auth/OtpRegistrationPage'));
const TermsAcceptancePage        = lazy(() => import('./features/auth/TermsAcceptancePage'));
const TermsFullTextPage          = lazy(() => import('./features/auth/TermsFullTextPage'));
const ProducerProfileForm         = lazy(() => import('./features/auth/ProducerProfileForm'));
const BuyerProfileForm            = lazy(() => import('./features/auth/BuyerProfileForm'));
const AdminVerificationDashboard  = lazy(() => import('./features/auth/AdminVerificationDashboard'));
const PhotoGallery                = lazy(() => import('./features/auth/PhotoGallery'));
const PriceDashboard              = lazy(() => import('./features/prices/PriceDashboard'));
const PriceEntryForm              = lazy(() => import('./features/prices/PriceEntryForm'));
const OfferForm                   = lazy(() => import('./features/marketplace/OfferForm'));
const OfferSearchPage             = lazy(() => import('./features/marketplace/OfferSearchPage'));
const MomoPaymentPage             = lazy(() => import('./features/marketplace/MomoPaymentPage'));
const TransactionConfirmPage      = lazy(() => import('./features/marketplace/TransactionConfirmPage'));
const TransactionHistoryPage      = lazy(() => import('./features/marketplace/TransactionHistoryPage'));
const DisputeForm                 = lazy(() => import('./features/marketplace/DisputeForm'));
const AdminDisputeDashboard       = lazy(() => import('./features/marketplace/AdminDisputeDashboard'));
const CooperativeOfferForm        = lazy(() => import('./features/marketplace/CooperativeOfferForm'));
const ThresholdAlertForm          = lazy(() => import('./features/sms-alerts/ThresholdAlertForm'));
const ProductSheetPage            = lazy(() => import('./features/marketplace/ProductSheetPage'));
const RevenueDashboard            = lazy(() => import('./features/admin/RevenueDashboard'));
const TraceabilitySheet           = lazy(() => import('./features/traceability/ProductSheetPage'));
const ProductSheetForm            = lazy(() => import('./features/traceability/ProductSheetForm'));
const QrCodeDisplay               = lazy(() => import('./features/traceability/QrCodeDisplay'));
const TransferForm                = lazy(() => import('./features/traceability/TransferForm'));
const AdminCollectorManager       = lazy(() => import('./features/admin/AdminCollectorManager'));
const MessageFlagsReviewPage      = lazy(() => import('./features/admin/MessageFlagsReviewPage'));
const AdminDisbursementDashboard  = lazy(() => import('./features/admin/AdminDisbursementDashboard'));
const PaymentNotificationsPage    = lazy(() => import('./features/escrow/PaymentNotificationsPage'));
const AccountSanctionsPage        = lazy(() => import('./features/admin/AccountSanctionsPage'));
const AdminAppealsPage            = lazy(() => import('./features/admin/AdminAppealsPage'));
const AccountStatusPage           = lazy(() => import('./features/auth/AccountStatusPage'));

const PageFallback = (): React.ReactElement => (
  <div className="flex flex-col items-center justify-center min-h-screen gap-3 bg-gris-fond">
    <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="#2D8A4E" strokeWidth="2">
      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 00-9-9" strokeLinecap="round" />
    </svg>
    <p className="text-sm text-txt-tertia font-medium">Chargement…</p>
  </div>
);

function DesktopShell(): React.ReactElement {
  return (
    <div className="min-h-screen bg-[#D6D3CC] md:flex md:items-start md:justify-center">
      <div className="w-full md:max-w-[480px] md:min-h-screen md:shadow-[0_0_40px_rgba(0,0,0,0.12)] relative bg-gris-fond">
        <Outlet />
      </div>
    </div>
  );
}

function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Pages admin — plein écran, sans DesktopShell */}
          <Route path="/admin/verify"                    element={<AdminRoute><AdminVerificationDashboard /></AdminRoute>} />
          <Route path="/admin/disputes"                  element={<AdminRoute><AdminDisputeDashboard /></AdminRoute>} />
          <Route path="/admin/revenue"                   element={<AdminRoute><RevenueDashboard /></AdminRoute>} />
          <Route path="/admin/collectors"                element={<AdminRoute><AdminCollectorManager /></AdminRoute>} />
          <Route path="/admin/messages"                  element={<AdminRoute><MessageFlagsReviewPage /></AdminRoute>} />
          <Route path="/admin/disbursements"             element={<AdminRoute><AdminDisbursementDashboard /></AdminRoute>} />
          <Route path="/admin/sanctions"                 element={<AdminRoute><AccountSanctionsPage /></AdminRoute>} />
          <Route path="/admin/appeals"                   element={<AdminRoute><AdminAppealsPage /></AdminRoute>} />

          {/* Toutes les autres pages — enveloppées dans le shell 480px */}
          <Route element={<DesktopShell />}>
            <Route path="/"                                element={<Navigate to="/marketplace/offers" replace />} />
            <Route path="/auth/register"                   element={<OtpRegistrationPage />} />
            <Route path="/auth/terms"                      element={<ProtectedRoute><TermsAcceptancePage /></ProtectedRoute>} />
            <Route path="/terms"                           element={<TermsFullTextPage />} />
            <Route path="/account/status"                  element={<ProtectedRoute><AccountStatusPage /></ProtectedRoute>} />
            <Route path="/profile/producer"                element={<ProtectedRoute><ProducerProfileForm /></ProtectedRoute>} />
            <Route path="/profile/buyer"                   element={<ProtectedRoute><BuyerProfileForm /></ProtectedRoute>} />
            <Route path="/profile/:id/photos"              element={<ProtectedRoute><PhotoGallery /></ProtectedRoute>} />
            <Route path="/prices"                          element={<ProtectedRoute><PriceDashboard /></ProtectedRoute>} />
            <Route path="/prices/entry"                    element={<ProtectedRoute><PriceEntryForm /></ProtectedRoute>} />
            <Route path="/marketplace/offers"              element={<ProtectedRoute><OfferSearchPage /></ProtectedRoute>} />
            <Route path="/marketplace/offers/new"          element={<ProtectedRoute><OfferForm /></ProtectedRoute>} />
            <Route path="/marketplace/offers/cooperative"  element={<ProtectedRoute><CooperativeOfferForm /></ProtectedRoute>} />
            <Route path="/marketplace/offers/:id"          element={<ProtectedRoute><ProductSheetPage /></ProtectedRoute>} />
            <Route path="/transactions"                    element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
            <Route path="/notifications"                   element={<ProtectedRoute><PaymentNotificationsPage /></ProtectedRoute>} />
            <Route path="/transactions/:id/pay"            element={<ProtectedRoute><MomoPaymentPage /></ProtectedRoute>} />
            {/* Pas de lien interne vers cette route par conception — voir le commentaire en tête de TransactionConfirmPage.tsx */}
            <Route path="/transactions/:id/confirm"        element={<ProtectedRoute><TransactionConfirmPage /></ProtectedRoute>} />
            <Route path="/transactions/:id/dispute"        element={<ProtectedRoute><DisputeForm /></ProtectedRoute>} />
            <Route path="/alerts"                          element={<ProtectedRoute><ThresholdAlertForm /></ProtectedRoute>} />
            <Route path="/t/:token"                        element={<TraceabilitySheet />} />
            <Route path="/traceability/new"                element={<ProtectedRoute><ProductSheetForm /></ProtectedRoute>} />
            <Route path="/traceability/:token/qr"          element={<ProtectedRoute><QrCodeDisplay /></ProtectedRoute>} />
            <Route path="/traceability/:token/transfer"    element={<ProtectedRoute><TransferForm /></ProtectedRoute>} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
